import React, { useEffect, useState } from 'react';
import { Box, Chip, Divider, IconButton } from '@material-ui/core';
import Moment from 'moment-timezone';

import { useTranslation } from 'react-i18next';

import CloseIcon from '@material-ui/icons/Close';

import useStyle from './style';

import ConnectCode, { ConnectCodeProps } from 'pages/connect/setup/connect.code';
import { stringifyWithErorHandling } from 'layout/helpers/util';
import { RequestLog, RequestLogsStatus } from 'types/interface/request-logs.interface';
import { getConnectionServiceName } from 'helpers/common.helper';

interface Props {
  requestLog?: RequestLog;
  handleClose?: () => void;
}

export default function RequestLogDetail({ requestLog, handleClose }: Props): JSX.Element {
  const classes = useStyle();
  const { t } = useTranslation();
  const [formattedRequestLogData, setFormattedRequestLogData] = useState(null);
  const [payloadCode, setPayloadCode] = useState<ConnectCodeProps>({
    code: '',
    header3: 'Request Body',
    identifier: 'payload',
    language: 'json',
  });
  const [responseCode, setResponseCode] = useState<ConnectCodeProps>({
    code: '',
    header3: 'Response Body',
    identifier: 'response',
    language: 'json',
  });
  const [copied, setCopied] = useState('');

  useEffect(() => {
    setCopied('');
    requestLog && formatRequestLogData(requestLog);
    const payloadCodeString = stringifyWithErorHandling(requestLog.payload);
    const responseCodeString = stringifyWithErorHandling(requestLog?.responsePayload);
    setPayloadCode({
      code:
        payloadCodeString === '{}'
          ? t('DASHBOARD_REQUEST_LOGS_SENSITIVE_REQUEST')
          : (payloadCodeString as string),
      header3: 'Request Body',
      identifier: 'payload',
      language: 'plaintext',
    });
    setResponseCode({
      code: (responseCodeString as string) || t('DASHBOARD_REQUEST_LOGS_SENSITIVE_RESPONSE'),
      header3: 'Response Body',
      identifier: 'response',
      language: 'plaintext',
    });
  }, [requestLog]);

  const formatRequestLogData = (requestLogDetails): void => {
    const formattedData = {
      STATUS: requestLogDetails.statusCode || '-',
      'REQUEST ID': requestLogDetails.requestId || '-',
      'BUSINESS NAME': requestLogDetails.businessName || '-',
      'CONNECTION UUID': requestLogDetails.connectionId || '-',
      'SERVICE NAME':
        getConnectionServiceName({ serviceName: requestLogDetails.serviceName }) || '-',
      URL: requestLogDetails.url,
      'USER UUID': requestLogDetails.userUuid || '-',
      SOURCE: requestLogDetails.source || '-',
      TIMESTAMP: Moment(requestLogDetails.timestamp).format('DD-MMM-YYYY hh:mm:ss A'),
    };

    setFormattedRequestLogData(formattedData);
  };

  return (
    <>
      <div className={classes.logDetail} data-testid="request-log-detail-tab">
        <Box className={`${classes.detailHeader} ${classes.smallPaddingLeft} `}>
          <div>
            <IconButton aria-label="delete" size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.smallPaddingLeft}>
            <Chip
              className={`${classes.chip} ${
                requestLog.status === RequestLogsStatus.success
                  ? classes.chipActive
                  : classes.chipInactive
              }`}
              data-testid={`request-log-status-chip`}
              label={requestLog.statusCode}
              size="small"
            />
          </div>
          <div className={`${classes.headerText} ${classes.smallPaddingLeft}`}>
            {requestLog?.method.toUpperCase()}
          </div>
          <Divider className={classes.vDivider} orientation="vertical" variant="middle" flexItem />
          <div className={`${classes.headerText} ${classes.longText}`}>{requestLog?.url}</div>
        </Box>
        <Divider className={classes.hDivider} style={{ marginTop: '9px' }} />
        <Box data-testid="request-log-details">
          {formattedRequestLogData &&
            Object.keys(formattedRequestLogData).map((dataKey, index) => {
              return (
                <div className={classes.dataField} key={`${dataKey['REQUEST ID']}_${index}`}>
                  <div className={classes.dataKey}>{dataKey}:</div>
                  <div className={classes.dataValue}>{formattedRequestLogData[dataKey]}</div>
                </div>
              );
            })}
          {requestLog?.payload && Object.keys(requestLog.payload).length > 0 && (
            <>
              <Box className={classes.queryTableBox} data-testid="request-log-payload">
                <div className={`${classes.dataKey} ${classes.queryTableTitle}`}>
                  {t('DASHBOARD_REQUEST_LOGS_QUERY_PARAMETER')}:
                </div>
                <div className={classes.queryTable}>
                  <div className={`${classes.dataKey} ${classes.queryColHeader}`}>Key</div>
                  <div
                    // eslint-disable-next-line max-len
                    className={`${classes.dataKey} ${classes.queryColHeader} ${classes.queryValCol}`}
                  >
                    Value
                  </div>
                </div>
                {Object.keys(requestLog.payload).map((queryKey, index) => {
                  return (
                    <div className={classes.queryTable} key={`${queryKey}_${index}`}>
                      <div className={classes.queryRow}>{queryKey}</div>
                      <div className={`${classes.queryRow} ${classes.queryValCol}`}>
                        {requestLog.payload[queryKey].toString() || '-'}
                      </div>
                    </div>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
        <Divider className={classes.hDivider} />
        <Box className={classes.codeWrapper} data-testid="request-log-payload">
          <ConnectCode {...payloadCode} setCopied={setCopied} />
          {copied && copied === payloadCode?.identifier && (
            <span className={classes.copiedText}>{t('DASHBOARD_CONNECT_COPIED')}</span>
          )}
        </Box>
        <Divider className={classes.hDivider} />
        <Box className={classes.codeWrapper} data-testid="request-log-response">
          <ConnectCode {...responseCode} setCopied={setCopied} />
          {copied && copied === responseCode?.identifier && (
            <span className={classes.copiedText}>{t('DASHBOARD_CONNECT_COPIED')}</span>
          )}
        </Box>
      </div>
    </>
  );
}
